<script lang="ts" setup>
import type { EmblaCarouselType } from 'embla-carousel'
import AutoScroll from 'embla-carousel-auto-scroll'
import emblaCarouselVue from 'embla-carousel-vue'

import CarouselButton from '~/components/carousels/CarouselButton.vue'

const props = withDefaults(
  defineProps<{
    dragFree?: boolean
    isAutoplay?: boolean
    isLoop?: boolean
    showButtons?: boolean
    showDots?: boolean
  }>(),
  { isAutoplay: false, isLoop: false, dragFree: false, showButtons: true, showDots: false }
)

const emit = defineEmits<{
  (e: 'carousel-near-end'): void
}>()

const [emblaRef, emblaApi] = emblaCarouselVue({
  align: 'start',
  dragFree: props.dragFree,
  loop: props.isLoop
}, [
  AutoScroll({ playOnInit: false, speed: 1 })
])

const canScrollNext = ref(false)
const canScrollPrev = ref(false)
const scrollSnaps = ref<number[]>([]);
const selectedIndex = ref(0)

let isLoading = false

onMounted(() => {
  if (!emblaApi?.value) {
    return
  }

  emblaApi.value.on('init', onSelect)
  emblaApi.value.on('reInit', onSelect)
  emblaApi.value.on('scroll', onScroll)
  emblaApi.value.on('select', onSelect)

  if (props.isAutoplay) {
    startAutoplay()
  }
})

onUnmounted(() => {
  if (props.isAutoplay) {
    stopAutoplay()
  }
})

function onScroll (emblaApi: EmblaCarouselType): void {
  if (isLoading) {
    return
  }

  const scrollProgress = emblaApi.scrollProgress()

  if (scrollProgress && scrollProgress >= 0.9) {
    isLoading = true

    emit('carousel-near-end')

    setTimeout(() => {
      isLoading = false
    }, 1000)
  }
}

function onSelect (emblaApi: EmblaCarouselType): void {
  canScrollNext.value = emblaApi.canScrollNext() ?? false
  canScrollPrev.value = emblaApi.canScrollPrev() ?? false
  scrollSnaps.value = emblaApi.scrollSnapList() ?? []
  selectedIndex.value = emblaApi.selectedScrollSnap() ?? 0

  if (props.isAutoplay) {
    startAutoplay()
  }
}

function scrollNext (): void {
  emblaApi?.value?.scrollNext()
}

function scrollPrev (): void {
  emblaApi?.value?.scrollPrev()
}

function scrollTo (index: number): void {
  emblaApi?.value?.scrollTo(index)
}

function startAutoplay (): void {
  const autoScroll = emblaApi?.value?.plugins()?.autoScroll

  if (autoScroll) {
    autoScroll.play()
  }

  return
}

function stopAutoplay (): void {
  const autoScroll = emblaApi?.value?.plugins()?.autoScroll

  if (autoScroll) {
    autoScroll.stop()
  }

  return
}

defineExpose({ scrollTo, selectedIndex })
</script>

<template>
  <div class="container gallery-container relative">
    <div ref="emblaRef" class="embla-content z-[1] overflow-hidden">
      <slot />
    </div>

    <CarouselButton
      v-if="showButtons && canScrollPrev"
      class="embla-button embla-button_left"
      is-gallery
      :is-next="false"
      @on-button-click="scrollPrev"
    />

    <CarouselButton
      v-if="showButtons && canScrollNext"
      class="embla-button embla-button_right"
      is-gallery
      is-next
      @on-button-click="scrollNext"
    />

    <div
      v-if="showDots"
      class="
        embla-dots absolute bottom-0 left-0 flex w-min items-center justify-center rounded-lg bg-white p-2 lg:hidden
      "
    >
      <div
        v-for="x in scrollSnaps.length"
        :key="x"
        class="embla-dot m-1 box-border size-2 rounded-full bg-[#dadada]"
        :class="{ '!bg-main-green': x === selectedIndex + 1 }"
      />
    </div>
  </div>
</template>
